"use client";

import { useEffect, useRef } from "react";

/**
 * Периодический вызов, который молчит, пока вкладка не видна.
 *
 * KAN-935. Опрос в свёрнутой вкладке — самая дорогая и самая бессмысленная часть
 * нагрузки: разбор прод-логов вечера турнира показал, что вкладки живут часами,
 * пока человек сидит в Discord, и всё это время исправно долбят бэкенд данными,
 * на которые никто не смотрит. До этого хука гейт по видимости был скопирован
 * руками в двух местах из девяти, а в семи его просто не было.
 *
 * Два правила, ради которых это отдельный хук, а не `setInterval` на месте:
 *
 * 1. **Тик в скрытой вкладке пропускается**, а не откладывается. Браузер и сам
 *    душит таймеры в фоне, но не до нуля: `setInterval` на 30 секунд в скрытой
 *    вкладке продолжает срабатывать примерно раз в минуту — этого достаточно,
 *    чтобы сотня забытых вкладок держала заметный поток запросов.
 * 2. **Возврат на вкладку догоняет данные сразу.** Без этого человек, вернувшись,
 *    смотрел бы на устаревшее состояние ещё до целого интервала — то есть гейт
 *    по видимости чинил бы нагрузку ценой правильности. Догоняющий вызов ставит
 *    всё на место: пока не смотрят — тихо, как только посмотрели — свежее.
 *
 * `callback` можно передавать инлайн-стрелкой: он читается через ref, поэтому
 * смена его идентичности НЕ перезапускает таймер (иначе интервал сбрасывался бы
 * на каждый ререндер родителя и превращался бы в опрос на каждый рендер).
 *
 * @param callback   что вызывать на тике и при возврате на вкладку.
 * @param intervalMs интервал в миллисекундах; `null`/`false`/`0` — опрос выключен
 *                   полностью (ни тиков, ни догоняющего вызова).
 * @param options.catchUpOnVisible догонять при возврате на вкладку (по умолчанию да).
 */
export function useVisibleInterval(
  callback: () => void,
  intervalMs: number | null | false,
  options?: { catchUpOnVisible?: boolean },
): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const catchUpOnVisible = options?.catchUpOnVisible !== false;

  useEffect(() => {
    if (!intervalMs || intervalMs <= 0) return;
    if (typeof document === "undefined") return;

    const isVisible = () => document.visibilityState !== "hidden";

    const timer = setInterval(() => {
      if (!isVisible()) return;
      callbackRef.current();
    }, intervalMs);

    if (!catchUpOnVisible) {
      return () => clearInterval(timer);
    }

    const onVisibilityChange = () => {
      if (isVisible()) callbackRef.current();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [intervalMs, catchUpOnVisible]);
}
